import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Modal,
  ProgressBar,
  ListGroup,
  Dropdown,
  InputGroup,
  Collapse,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faProjectDiagram,
  faPlus,
  faEdit,
  faTrash,
  faList,
  faCalendarAlt,
  faUsers,
  faFilter,
  faClock,
  faEllipsisV,
  faSearch,
  faExclamationTriangle,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
} from "../api/projects";
import { getTasks, createTask, updateTask, deleteTask } from "../api/tasks";
import toast from "../utils/toastUtils";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { getCommittees, getMembers, getMeetings } from "../api/api";
import LotusIcon from "../components/LotusIcon";
import queryString from "query-string";

// Custom styles to ensure dropdowns aren't cut off
const styles = {
  dropdownMenu: {
    zIndex: 1050,
    position: "absolute",
    transform: "none",
    willChange: "transform",
  },
};

const ProjectsPage = () => {
  const { currentUser } = useAuth();

  // State for projects and loading
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add state for users and committees
  const [users, setUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);

  // State for project management
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [formMode, setFormMode] = useState("create"); // 'create' or 'edit'

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "Planning",
    startDate: "",
    endDate: "",
    members: [],
    committee: "",
    progress: 0,
  });

  // New task form state
  const [taskFormData, setTaskFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    dueDate: "",
    assignedTo: "",
    projectId: null,
    relationships: {
      project: null,
      committee: null,
      meeting: null,
    },
    labels: [],
    createdBy: currentUser?._id,
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: "",
    committee: "",
    search: "",
  });

  // New member input
  const [newMember, setNewMember] = useState("");

  // Filter UI state - collapsible section
  const [showFilters, setShowFilters] = useState(false);

  // New label input
  const [newLabel, setNewLabel] = useState("");

  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
    fetchTasks();
  }, []);

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getMembers();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
        toast.error("Failed to load users");
      }
    };
    fetchUsers();
  }, []);

  // Fetch committees on component mount
  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        const data = await getCommittees();
        setCommittees(data);
      } catch (err) {
        console.error("Failed to fetch committees:", err);
        toast.error("Failed to load committees");
      }
    };
    fetchCommittees();
  }, []);

  // Fetch meetings on component mount
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const data = await getMeetings();
        setMeetings(data);
      } catch (err) {
        console.error("Failed to fetch meetings:", err);
        toast.error("Failed to load meetings");
      }
    };
    fetchMeetings();
  }, []);

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProjects();
      setProjects(data);
    } catch (err) {
      setError(err.message || "Failed to fetch projects");
      toast.error(err.message || "Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  // Add a function to fetch tasks
  const fetchTasks = async () => {
    try {
      const data = await getTasks();
      setTasks(data);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    }
  };

  // Open modal to create a new project
  const handleCreateProject = () => {
    setFormData({
      title: "",
      description: "",
      status: "Planning",
      startDate: "",
      endDate: "",
      members: [],
      committee: "",
      progress: 0,
    });
    setFormMode("create");
    setShowProjectModal(true);
  };

  // Open modal to edit an existing project
  const handleEditProject = (project) => {
    setCurrentProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      status: project.status,
      startDate: project.startDate?.split("T")[0],
      endDate: project.endDate?.split("T")[0],
      members: project.members?.map((member) => member._id) || [],
      committee: project.committee,
      progress: project.progress,
    });
    setFormMode("edit");
    setShowProjectModal(true);
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this project? This will also delete all associated tasks."
      )
    ) {
      try {
        await deleteProject(projectId);
        toast.success("Project deleted successfully");
        fetchProjects(); // Refresh the projects list
        fetchTasks(); // Also refresh tasks list
      } catch (err) {
        toast.error(err.message || "Failed to delete project");
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Add a new team member
  const handleAddMember = () => {
    if (newMember.trim() && !formData.members.includes(newMember)) {
      setFormData({
        ...formData,
        members: [...formData.members, newMember.trim()],
      });
      setNewMember("");
    }
  };

  // Remove a team member
  const handleRemoveMember = (memberToRemove) => {
    setFormData({
      ...formData,
      members: formData.members.filter((member) => member !== memberToRemove),
    });
  };

  // Handle project form submission
  const handleSubmitProject = async () => {
    try {
      if (!formData.title) {
        toast.error("Project title is required");
        return;
      }

      const projectData = {
        ...formData,
        // Convert dates to ISO format
        startDate: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : undefined,
        endDate: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : undefined,
      };

      if (formMode === "create") {
        await createProject(projectData);
        toast.success("Project created successfully");
      } else {
        await updateProject(currentProject._id, projectData);
        toast.success("Project updated successfully");
      }
      setShowProjectModal(false);
      fetchProjects();
    } catch (err) {
      console.error("Project submission error:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          `Failed to ${formMode} project`
      );
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      status: "",
      committee: "",
      search: "",
    });
  };

  // Calculate project progress based on associated tasks
  const calculateProgress = (projectId) => {
    const projectTasks = tasks.filter((task) => task.projectId === projectId);
    if (projectTasks.length === 0) return 0;

    const completedTasks = projectTasks.filter(
      (task) => task.status === "Done"
    );
    return Math.round((completedTasks.length / projectTasks.length) * 100);
  };

  // Open modal to add a new task to a project
  const handleAddTask = async (projectId) => {
    const project = projects.find((p) => p._id === projectId);
    setCurrentProject(project);
    setTaskFormData({
      title: "",
      description: "",
      status: "To Do",
      priority: "Medium",
      dueDate: "",
      assignedTo: "",
      projectId: projectId,
      relationships: {
        project: {
          id: projectId,
          name: project?.title || "Current Project",
        },
        committee: null,
        meeting: null,
      },
      labels: [],
      createdBy: currentUser?._id,
    });
    setShowTaskModal(true);
  };

  // Handle task form input changes
  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData({
      ...taskFormData,
      [name]: value,
    });
  };

  // Handle relationship changes for tasks
  const handleTaskRelationshipChange = (type, value) => {
    if (!value) {
      // If empty value, remove the relationship
      setTaskFormData({
        ...taskFormData,
        relationships: {
          ...taskFormData.relationships,
          [type]: null,
        },
      });
      return;
    }

    let name;
    switch (type) {
      case "project":
        const project = projects.find((p) => p._id === value);
        name = project ? project.title : "Unknown Project";
        break;
      case "committee":
        const committee = committees.find((c) => c._id === value);
        name = committee ? committee.name : "Unknown Committee";
        break;
      case "meeting":
        const meeting = meetings.find((m) => m.id === Number(value));
        name = meeting
          ? `${meeting.name} (${meeting.date})`
          : "Unknown Meeting";
        break;
      default:
        name = "Unknown";
    }

    setTaskFormData({
      ...taskFormData,
      relationships: {
        ...taskFormData.relationships,
        [type]: { id: value, name },
      },
    });
  };

  // Handle adding a label to a task
  const handleAddTaskLabel = () => {
    if (newLabel.trim() && !taskFormData.labels.includes(newLabel.trim())) {
      setTaskFormData({
        ...taskFormData,
        labels: [...taskFormData.labels, newLabel.trim()],
      });
      setNewLabel("");
    }
  };

  // Handle removing a label from a task
  const handleRemoveTaskLabel = (labelToRemove) => {
    setTaskFormData({
      ...taskFormData,
      labels: taskFormData.labels.filter((label) => label !== labelToRemove),
    });
  };

  // Handle task form submission
  const handleSubmitTask = async () => {
    try {
      if (!taskFormData.title) {
        toast.error("Task title is required");
        return;
      }

      // Check if user is logged in
      if (!currentUser?._id) {
        toast.error("You must be logged in to create a task");
        return;
      }

      // Create a clean task object with valid fields
      const taskData = {
        title: taskFormData.title,
        createdBy: currentUser._id,
        labels: taskFormData.labels,
      };

      // Add optional fields only if they have values
      if (taskFormData.description && taskFormData.description.trim()) {
        taskData.description = taskFormData.description;
      }

      if (taskFormData.status) {
        taskData.status = taskFormData.status;
      }

      if (taskFormData.priority) {
        taskData.priority = taskFormData.priority;
      }

      // Convert date to ISO format only if it exists
      if (taskFormData.dueDate && taskFormData.dueDate.trim()) {
        taskData.dueDate = new Date(taskFormData.dueDate).toISOString();
      }

      // Only include assignedTo if it's a non-empty value
      if (taskFormData.assignedTo && taskFormData.assignedTo.trim()) {
        taskData.assignedTo = taskFormData.assignedTo;
      }

      // Process relationships
      if (taskFormData.projectId) {
        taskData.projectId = taskFormData.projectId;
      }

      // Handle relationships
      const firstRelationship = Object.entries(taskFormData.relationships).find(
        ([_, data]) => data !== null
      );

      if (firstRelationship) {
        const [type, data] = firstRelationship;
        if (data && data.id && data.name) {
          taskData.relatedTo = {
            type,
            id: data.id,
            name: data.name,
          };
          if (type === "project") {
            taskData.projectId = data.id;
          }
        }
      }

      console.log("Sending task data:", taskData);

      await createTask(taskData);
      toast.success("Task created successfully");
      setShowTaskModal(false);
      fetchProjects(); // Refresh projects
      fetchTasks(); // Also refresh tasks
    } catch (err) {
      console.error("Task submission error:", err);
      toast.error(
        err.response?.data?.message || err.message || "Failed to create task"
      );
    }
  };

  // Filter projects
  const filteredProjects = projects.filter((project) => {
    return (
      (filters.status === "" || project.status === filters.status) &&
      (filters.committee === "" || project.committee === filters.committee) &&
      (filters.search === "" ||
        project.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        project.description
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()))
    );
  });

  // Get progress bar variant based on progress and status
  const getProgressVariant = (progress, status) => {
    if (status === "Completed") return "success";
    if (progress < 25) return "danger";
    if (progress < 75) return "warning";
    return "success";
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Planning":
        return "secondary";
      case "In Progress":
        return "primary";
      case "On Hold":
        return "warning";
      case "Completed":
        return "success";
      default:
        return "secondary";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get project tasks
  const getProjectTasks = (projectId) => {
    return tasks.filter((task) => {
      // Handle both direct ID and populated object with _id
      const taskProjectId = task.projectId?._id
        ? task.projectId._id
        : task.projectId;
      return taskProjectId === projectId;
    });
  };

  // Get priority badge color
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return "danger";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "secondary";
    }
  };

  // Add a function to handle quick status changes
  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      toast.success("Status updated successfully");
      fetchProjects(); // Refresh the projects list
      fetchTasks(); // Also refresh tasks list
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  // Get related badge color
  const getRelatedBadgeColor = (type) => {
    switch (type) {
      case "project":
        return "primary";
      case "committee":
        return "info";
      case "meeting":
        return "warning";
      default:
        return "secondary";
    }
  };

  return (
    <Container className="py-3">
      <div className="section-title d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <LotusIcon className="me-3" size={32} />
          <h1 className="mb-0">Projects</h1>
        </div>
        <div>
          <Button
            variant="outline-primary"
            onClick={() => setShowFilters(!showFilters)}
            className="me-2"
            style={{
              borderColor: "#f5c2c2",
              color: "#d9669b",
            }}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
            <FontAwesomeIcon
              icon={showFilters ? faChevronUp : faChevronDown}
              className="ms-2"
              size="xs"
            />
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProject}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
              fontWeight: "600",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Filters - Collapsible */}
      <Collapse in={showFilters}>
        <div>
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Header
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <h5 className="mb-0">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="me-2"
                  style={{ color: "#d9669b" }}
                />
                Filter Projects
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          background: "#fff0f5",
                          border: "1px solid #f5c2c2",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faSearch}
                          style={{ color: "#d9669b" }}
                        />
                      </InputGroup.Text>
                      <Form.Control
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        placeholder="Search projects..."
                        style={{ borderLeft: "none" }}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Statuses</option>
                      <option value="Planning">Planning</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Committee</Form.Label>
                    <Form.Select
                      name="committee"
                      value={filters.committee}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Committees</option>
                      {committees.map((committee) => (
                        <option key={committee._id} value={committee._id}>
                          {committee.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3 d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearFilters}
                    className="w-100"
                    style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {/* Projects List */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-5 section">
          <FontAwesomeIcon
            icon={faProjectDiagram}
            style={{ fontSize: "3rem", color: "#d9669b", opacity: 0.5 }}
          />
          <h3 className="mt-3" style={{ color: "#d9669b" }}>
            No projects found
          </h3>
          <p className="text-muted">
            {filters.search || filters.status || filters.committee
              ? "Try adjusting your filters"
              : "Create your first project"}
          </p>
          <Button
            variant="primary"
            onClick={handleCreateProject}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create New Project
          </Button>
        </div>
      ) : (
        <Row className="g-4">
          {filteredProjects.map((project) => (
            <Col md={6} lg={4} key={project._id}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderRadius: "12px", overflow: "hidden" }}
              >
                <Card.Header
                  style={{
                    background: "linear-gradient(to right, #fff0f5, #fff8dc)",
                    borderBottom: "1px solid #f5c2c2",
                    padding: "1rem",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5
                      className="mb-0"
                      style={{
                        color: "#d9669b",
                        textAlign: "left",
                        fontSize: "1.15rem",
                      }}
                    >
                      {project.title}
                    </h5>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        style={{ border: "none" }}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={styles.dropdownMenu}>
                        <Dropdown.Item
                          onClick={() => handleEditProject(project)}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-2" />
                          Edit Project
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleAddTask(project._id)}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" />
                          Add Task
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => handleDeleteProject(project._id)}
                          className="text-danger"
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Delete Project
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </Card.Header>
                <Card.Body className="pt-3 pb-0">
                  {project.description && (
                    <p className="text-muted small mb-3">
                      {project.description}
                    </p>
                  )}

                  <div className="d-flex justify-content-between mb-3">
                    <Badge
                      bg={getStatusBadgeColor(project.status)}
                      className="sasc-badge"
                    >
                      {project.status}
                    </Badge>
                    {project.committee && (
                      <Badge bg="info" className="sasc-badge">
                        {project.committee}
                      </Badge>
                    )}
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">Progress</small>
                      <small>{project.progress}%</small>
                    </div>
                    <ProgressBar
                      now={project.progress}
                      variant={getProgressVariant(
                        project.progress,
                        project.status
                      )}
                      style={{ height: "8px" }}
                    />
                  </div>

                  <div className="mb-3 d-flex flex-wrap">
                    <div className="d-flex align-items-center text-muted small me-3">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="me-2"
                        style={{ color: "#d9669b", width: "16px" }}
                      />
                      <div>
                        <strong>Timeline:</strong>
                        <br />
                        {formatDate(project.startDate)} -{" "}
                        {formatDate(project.endDate)}
                      </div>
                    </div>
                  </div>

                  {/* Project Tasks */}
                  <div className="mt-4">
                    <h6 className="mb-2 d-flex justify-content-between align-items-center">
                      <span>
                        <FontAwesomeIcon
                          icon={faList}
                          className="me-2"
                          style={{ color: "#d9669b" }}
                        />
                        Tasks ({getProjectTasks(project._id).length})
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleAddTask(project._id)}
                        style={{
                          borderColor: "#f5c2c2",
                          color: "#d9669b",
                          fontSize: "0.75rem",
                          padding: "0.2rem 0.5rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add
                      </Button>
                    </h6>
                    {getProjectTasks(project._id).length === 0 ? (
                      <div className="text-center py-3 border rounded mb-3">
                        <p className="text-muted mb-0 small">No tasks yet</p>
                      </div>
                    ) : (
                      <ListGroup
                        variant="flush"
                        className="border rounded mb-3"
                      >
                        {getProjectTasks(project._id)
                          .slice(0, 2) // Show fewer tasks to reduce clutter
                          .map((task) => (
                            <ListGroup.Item
                              key={task._id}
                              className="py-2 px-3 border-bottom"
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div
                                    style={{
                                      color:
                                        task.status === "Done"
                                          ? "#28a745"
                                          : "inherit",
                                      textAlign: "left",
                                    }}
                                  >
                                    {task.status === "Done" ? (
                                      <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="me-2 text-success"
                                      />
                                    ) : (
                                      <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className={`me-2 text-${getPriorityBadgeColor(
                                          task.priority
                                        )}`}
                                      />
                                    )}
                                    {task.title}
                                  </div>
                                  <small className="text-muted d-block">
                                    {formatDate(task.dueDate)}
                                  </small>
                                </div>
                                <div style={{ position: "relative" }}>
                                  <Dropdown align="end">
                                    <Dropdown.Toggle
                                      variant={
                                        task.status === "Done"
                                          ? "success"
                                          : getPriorityBadgeColor(task.priority)
                                      }
                                      size="sm"
                                      className="py-0 px-2"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      <span className="me-1">
                                        {task.status}
                                      </span>
                                      {task.status !== "Done" && (
                                        <span className="ms-1">
                                          • {task.priority}
                                        </span>
                                      )}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu
                                      size="sm"
                                      style={styles.dropdownMenu}
                                    >
                                      <Dropdown.Item
                                        onClick={() =>
                                          handleQuickStatusChange(
                                            task._id,
                                            "To Do"
                                          )
                                        }
                                        active={task.status === "To Do"}
                                      >
                                        To Do
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          handleQuickStatusChange(
                                            task._id,
                                            "In Progress"
                                          )
                                        }
                                        active={task.status === "In Progress"}
                                      >
                                        In Progress
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          handleQuickStatusChange(
                                            task._id,
                                            "Done"
                                          )
                                        }
                                        active={task.status === "Done"}
                                      >
                                        Done
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                              </div>
                            </ListGroup.Item>
                          ))}
                        {getProjectTasks(project._id).length > 2 && (
                          <ListGroup.Item className="text-center py-2">
                            <Link
                              to={`/tasks?projectId=${project._id}`}
                              style={{
                                color: "#d9669b",
                                textDecoration: "none",
                                fontSize: "0.825rem",
                              }}
                            >
                              View all {getProjectTasks(project._id).length}{" "}
                              tasks
                            </Link>
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    )}
                  </div>
                </Card.Body>
                {project.members && project.members.length > 0 && (
                  <Card.Footer
                    className="bg-light"
                    style={{ borderTop: "1px solid #f5c2c2" }}
                  >
                    <small className="text-muted">Team:</small>
                    <div className="mt-1 d-flex flex-wrap">
                      {project.members.slice(0, 3).map((member, index) => (
                        <Badge
                          key={index}
                          bg="light"
                          text="dark"
                          className="me-1 mb-1"
                          style={{ border: "1px solid #f5c2c2" }}
                        >
                          {typeof member === "object" ? member.name : member}
                        </Badge>
                      ))}
                      {project.members.length > 3 && (
                        <Badge
                          bg="light"
                          text="dark"
                          className="me-1 mb-1"
                          style={{ border: "1px solid #f5c2c2" }}
                        >
                          +{project.members.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Project Modal */}
      <Modal
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            <FontAwesomeIcon icon={faProjectDiagram} className="me-2" />
            {formMode === "create" ? "Create New Project" : "Edit Project"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Project Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter project title"
                required
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter project description"
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="Planning">Planning</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Committee</Form.Label>
                  <Form.Select
                    name="committee"
                    value={formData.committee}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="">Select Committee</option>
                    {committees.map((committee) => (
                      <option key={committee._id} value={committee._id}>
                        {committee.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Team Members</Form.Label>
              <Form.Select
                multiple
                name="members"
                value={formData.members}
                onChange={(e) => {
                  const selectedOptions = Array.from(
                    e.target.selectedOptions,
                    (option) => option.value
                  );
                  setFormData({
                    ...formData,
                    members: selectedOptions,
                  });
                }}
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                  height: "120px",
                }}
              >
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
              <small className="text-muted">
                Hold Ctrl/Cmd to select multiple members
              </small>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowProjectModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitProject}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            {formMode === "create" ? "Create Project" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Task Modal */}
      <Modal
        show={showTaskModal}
        onHide={() => setShowTaskModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            <FontAwesomeIcon icon={faTasks} className="me-2" />
            Add Task to Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={taskFormData.title}
                onChange={handleTaskInputChange}
                placeholder="Enter task title"
                required
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={taskFormData.description}
                onChange={handleTaskInputChange}
                placeholder="Enter task description"
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={taskFormData.status}
                    onChange={handleTaskInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Done">Done</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={taskFormData.priority}
                    onChange={handleTaskInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={taskFormData.dueDate}
                    onChange={handleTaskInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>
              <Form.Select
                name="assignedTo"
                value={taskFormData.assignedTo}
                onChange={handleTaskInputChange}
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              >
                <option value="">Select Assignee</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Relationships</Form.Label>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project</Form.Label>
                    <Form.Select
                      value={taskFormData.relationships.project?.id || ""}
                      onChange={(e) =>
                        handleTaskRelationshipChange("project", e.target.value)
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a project</option>
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Committee</Form.Label>
                    <Form.Select
                      value={taskFormData.relationships.committee?.id || ""}
                      onChange={(e) =>
                        handleTaskRelationshipChange(
                          "committee",
                          e.target.value
                        )
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a committee</option>
                      {committees.map((committee) => (
                        <option key={committee._id} value={committee._id}>
                          {committee.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Meeting</Form.Label>
                    <Form.Select
                      value={taskFormData.relationships.meeting?.id || ""}
                      onChange={(e) =>
                        handleTaskRelationshipChange("meeting", e.target.value)
                      }
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #f5c2c2",
                      }}
                    >
                      <option value="">Select a meeting</option>
                      {meetings.map((meeting) => (
                        <option key={meeting.id} value={meeting.id}>
                          {meeting.name} ({meeting.date})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Display selected relationships */}
              <div className="mt-2">
                {Object.entries(taskFormData.relationships).map(
                  ([type, data]) => {
                    if (!data) return null;
                    return (
                      <Badge
                        key={type}
                        bg={getRelatedBadgeColor(type)}
                        className="me-2 mb-2"
                      >
                        <FontAwesomeIcon
                          icon={
                            type === "meeting"
                              ? faCalendarAlt
                              : type === "committee"
                              ? faUsers
                              : faList
                          }
                          className="me-1"
                        />
                        {data.name}
                        <span
                          onClick={() => handleTaskRelationshipChange(type, "")}
                          style={{ cursor: "pointer", marginLeft: "8px" }}
                        >
                          ×
                        </span>
                      </Badge>
                    );
                  }
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Labels</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Add a label"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTaskLabel();
                    }
                  }}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAddTaskLabel}
                  style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                >
                  Add
                </Button>
              </InputGroup>

              {taskFormData.labels.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {taskFormData.labels.map((label) => (
                    <Badge
                      key={label}
                      bg="light"
                      text="dark"
                      className="mb-1 d-flex align-items-center"
                      style={{ border: "1px solid #f5c2c2" }}
                    >
                      # {label}
                      <span
                        onClick={() => handleRemoveTaskLabel(label)}
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                      >
                        &times;
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowTaskModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitTask}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            Add Task
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectsPage;
